import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Mail } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { getProductBySlug } from "@/data/content";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  if (!product) {
    return (
      <div className="grain relative min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 pt-40 pb-24 text-center relative z-10">
          <h1 className="font-heading font-bold text-3xl mb-4">Solution introuvable</h1>
          <Link to="/#solutions" className="text-accent hover:underline">Voir nos solutions</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = product.icon;
  const contactHref = `/contact?sujet=${encodeURIComponent(product.title)}`;

  return (
    <div className="grain relative min-h-screen">
      <Helmet>
        <title>{`${product.title} | GMP Développement`}</title>
        <meta name="description" content={product.longDesc} />
        <link rel="canonical" href={`${origin}/solutions/${product.slug}`} />
        <meta property="og:title" content={`${product.title} — GMP Développement`} />
        <meta property="og:description" content={product.desc} />
        <meta property="og:image" content={product.detailImage} />
        <meta property="og:type" content="product" />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 sm:pb-20 overflow-hidden scroll-mt-24">
        <div className="absolute top-1/4 -left-20 w-[36rem] h-[36rem] bg-accent/12 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] bg-warm/12 blur-3xl rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center relative z-10">
          <div>
            <Link to="/#solutions" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent mb-8 transition-colors">
              <ArrowLeft size={16} /> Nos solutions
            </Link>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 flex items-center justify-center border border-accent/40 bg-accent/5 text-accent">
                <Icon size={22} />
              </div>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-warm border border-warm/30 px-3 py-1">
                {product.tag}
              </span>
            </div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.97]">
              {product.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              {product.longDesc}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link
                to={contactHref}
                data-testid="product-cta-contact"
                className="group bg-accent text-background font-semibold px-7 py-3.5 flex items-center justify-center gap-2 hover:bg-accent-hover transition-colors glow-accent"
              >
                Je veux cette solution
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/#tarifs"
                className="border border-border text-foreground px-7 py-3.5 text-center hover:border-warm/50 transition-colors"
              >
                Voir les tarifs
              </Link>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative border border-white/10 bg-surface overflow-hidden order-first lg:order-last"
          >
            <img src={product.detailImage} alt={product.title} className="w-full h-[280px] sm:h-[360px] lg:h-[460px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-transparent to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 sm:py-20 border-y border-border bg-surface/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-8">Pourquoi ça marche</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
            {product.benefits.map((b) => (
              <div key={b} className="flex items-start gap-3">
                <Check size={20} className="text-warm shrink-0 mt-0.5" />
                <span className="text-foreground/90">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-12">
            Comment ça fonctionne
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {product.how.map((s, i) => (
              <div key={s.t} className="relative bg-surface border border-border p-7">
                <span className="font-mono text-5xl font-bold text-white/5 absolute top-4 right-6">0{i + 1}</span>
                <h3 className="font-heading font-semibold text-lg mb-2 relative z-10">{s.t}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed relative z-10">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="border border-warm/30 bg-warm/5 p-8 sm:p-12 text-center">
            <h3 className="font-heading font-bold text-2xl sm:text-3xl mb-3">Prêt à passer à l'action ?</h3>
            <p className="text-muted-foreground mb-7 max-w-xl mx-auto">
              Parlez-nous de votre projet. On vous propose une solution sur mesure, humaine et efficace — réponse sous 24h.
            </p>
            <Link
              to={contactHref}
              data-testid="product-cta-contact-bottom"
              className="inline-flex items-center gap-2 bg-warm text-background font-semibold px-7 py-3.5 hover:bg-warm-soft transition-colors"
            >
              <Mail size={18} /> Prendre contact
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
